import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import {
    Backdrop,
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    Menu,
    MenuItem,
    Pagination,
    Select,
    Slider,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getFetch, postFetch } from '../../../fetch/fetch';

interface Branch { label: string }
interface NVR { branch: string; label: string }
interface Camera { id: string; nvr: string; branch: string; channel: number; label: string }
interface CameraData {
    branches: Record<string, Branch>;
    nvrs: Record<string, NVR>;
    cameras: Camera[];
}
interface CctvGroup { id: number; name: string; camera_ids: string[]; is_shared: boolean }

const CctvMain: React.FC<any> = (props) => {

    const [cameraData, setCameraData] = useState<CameraData | null>(null);
    const [groups, setGroups] = useState<CctvGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedBranch, setSelectedBranch] = useState("");
    const [selectedNvr, setSelectedNvr] = useState("");
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLive, setIsLive] = useState(true);
    const [playbackTime, setPlaybackTime] = useState<Date>(new Date());
    const [embedUrls, setEmbedUrls] = useState<Record<string, string>>({});
    const [groupSettingsCamera, setGroupSettingsCamera] = useState<Camera | null>(null);
    const [showNewGroupModal, setShowNewGroupModal] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [cellMenuAnchor, setCellMenuAnchor] = useState<{ el: HTMLElement; cam: Camera } | null>(null);
    const [expandedCamId, setExpandedCamId] = useState<string | null>(null);

    // 스트림 lifecycle refs
    const activeStreamsRef = useRef<Set<string>>(new Set());           // streamName 집합 — 모든 stop 추적
    const camStreamRef = useRef<Map<string, string>>(new Map());       // camId → streamName — expand/collapse 조회용
    const isMountedRef = useRef(true);
    const currentBatchIdRef = useRef(0); // 배치 세대 카운터 — 이전 in-flight 스트림 무효화용
    const streamTriggerDebounceRef = useRef<NodeJS.Timeout | null>(null);
    // playbackDebounceRef: ±5분 버튼 연속 클릭 방지용 (300ms)
    const playbackDebounceRef = useRef<NodeJS.Timeout | null>(null);

    // stale closure 방지 — 항상 최신값 참조
    const isLiveRef = useRef(isLive);
    isLiveRef.current = isLive;
    const playbackTimeRef = useRef(playbackTime);
    playbackTimeRef.current = playbackTime;
    const pageCamerasRef = useRef<Camera[]>([]);

    // 반응형 그리드
    const isDesktop = useMediaQuery('(min-width: 1200px)');
    const isTablet = useMediaQuery('(min-width: 900px)');
    const cols = isDesktop ? 4 : isTablet ? 3 : 2; // 8개 시 자동 4×2 레이아웃

    const isNvrMode = selectedGroupId === null;
    const limitToEight = isNvrMode && !isLive; // NVR 모드 + 녹화: 메인스트림 대역폭 제한
    const perPage = limitToEight
        ? (isDesktop ? 8 : isTablet ? 6 : 4)
        : (isDesktop ? 16 : isTablet ? 9 : 4);

    // 현재 표시할 카메라 목록
    const currentCameras = useMemo(() => {
        if (!cameraData) return [];
        if (selectedGroupId !== null) {
            const group = groups.find(g => g.id === selectedGroupId);
            return (group?.camera_ids || [])
                .map(id => cameraData.cameras.find(c => c.id === id))
                .filter(Boolean) as Camera[];
        }
        return cameraData.cameras.filter(c => c.nvr === selectedNvr);
    }, [cameraData, selectedNvr, selectedGroupId, groups]);

    const totalPages = Math.max(1, Math.ceil(currentCameras.length / perPage));

    const pageCameras = useMemo(() =>
        currentCameras.slice((currentPage - 1) * perPage, currentPage * perPage),
        [currentCameras, currentPage, perPage]
    );
    pageCamerasRef.current = pageCameras;

    // KST 날짜 포맷 (Z 없이)
    const formatLocalISO = (date: Date): string => {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    // 개별 스트림 시작 — batchId로 superseded/unmount 시 self-stop
    const startStream = useCallback(async (
        cam: Camera,
        batchId: number
    ): Promise<{ streamName: string; embedUrl: string } | null> => {
        try {
            let result: any;
            if (isLiveRef.current) {
                result = await postFetch("/api/cctv/live/start", { camId: cam.id, hd: false });
            } else {
                const endTime = new Date(playbackTimeRef.current.getTime() + 10 * 60 * 1000); // +10분
                result = await postFetch("/api/cctv/playback/start", {
                    camId: cam.id,
                    startTime: formatLocalISO(playbackTimeRef.current),
                    endTime: formatLocalISO(endTime)
                });
            }
            if (result?.streamName) {
                // HTTP 완료 후 유효성 검사 — 더 새 배치가 시작됐거나 unmount됐으면 즉시 stop
                if (batchId !== currentBatchIdRef.current || !isMountedRef.current) {
                    postFetch("/api/cctv/stream/stop", { streamName: result.streamName })
                        .catch(e => console.error(`[cctv self-stop fail] ${result.streamName}`, e));
                    return null;
                }
                activeStreamsRef.current.add(result.streamName);        // Set.add — 덮어쓰기 없음
                camStreamRef.current.set(cam.id, result.streamName);    // 조회 맵 업데이트
                return { streamName: result.streamName, embedUrl: result.embedUrl };
            }
        } catch (e) {
            console.log(e);
        }
        return null;
    }, []);

    // 모든 스트림 종료 (fire-and-forget)
    const stopAllStreams = useCallback(() => {
        const allStreams = Array.from(activeStreamsRef.current);
        activeStreamsRef.current.clear();
        camStreamRef.current.clear();
        allStreams.forEach(name => {
            postFetch("/api/cctv/stream/stop", { streamName: name })
                .catch(e => console.error(`[cctv stop fail] ${name}`, e));
        });
    }, []);

    // 페이지 스트림 시작 — Set 기반, clear 후 새 배치 추적
    const startPageStreams = useCallback(async (cameras: Camera[]) => {
        const myBatchId = ++currentBatchIdRef.current;

        // Set 전체 스냅샷 — camId 덮어쓰기 없이 모든 streamName 포착
        const oldStreamNames = Array.from(activeStreamsRef.current);
        // 즉시 clear — 새 배치가 빈 Set에서 시작 (stale 항목 자동 소멸)
        activeStreamsRef.current.clear();
        camStreamRef.current.clear();

        if (!isMountedRef.current) {
            oldStreamNames.forEach(name =>
                postFetch("/api/cctv/stream/stop", { streamName: name })
                    .catch(e => console.error(`[cctv stop fail] ${name}`, e))
            );
            return;
        }

        const results = await Promise.all(
            cameras.map(async cam => {
                const data = await startStream(cam, myBatchId);
                return { camId: cam.id, embedUrl: data?.embedUrl || null };
            })
        );

        // 이 배치가 최신이고 마운트 중일 때만 화면 업데이트
        if (myBatchId === currentBatchIdRef.current && isMountedRef.current) {
            const newUrls: Record<string, string> = {};
            results.forEach(({ camId, embedUrl }) => { if (embedUrl) newUrls[camId] = embedUrl; });
            setEmbedUrls(newUrls);
        }

        // 이전 스트림 stop — unmount 이후에도 항상 실행 (비동기 함수는 unmount 후에도 계속 실행)
        oldStreamNames.forEach(name =>
            postFetch("/api/cctv/stream/stop", { streamName: name })
                .catch(e => console.error(`[cctv stop fail] ${name}`, e))
        );
    }, [startStream]);

    // 탭/페이지/모드 변경 시 200ms debounce로 스트림 재시작
    // deps에 pageCameras 없음 → 그룹 편집이 스트림 재시작 유발하지 않음
    useEffect(() => {
        if (streamTriggerDebounceRef.current) clearTimeout(streamTriggerDebounceRef.current);
        streamTriggerDebounceRef.current = setTimeout(() => {
            const cameras = pageCamerasRef.current;
            if (cameras.length === 0) return;
            startPageStreams(cameras);
        }, 200);
        return () => {
            if (streamTriggerDebounceRef.current) clearTimeout(streamTriggerDebounceRef.current);
        };
    }, [currentPage, selectedNvr, selectedGroupId, isLive, startPageStreams]);

    // playbackTime 확정값 변경 시 스트림 재시작 (300ms debounce — ±5분 연속 클릭 보호)
    // DateTimePicker는 onAccept로만 playbackTime을 갱신하므로 입력 중 트리거 없음
    useEffect(() => {
        if (isLiveRef.current) return;
        if (playbackDebounceRef.current) clearTimeout(playbackDebounceRef.current);
        playbackDebounceRef.current = setTimeout(() => {
            const cameras = pageCamerasRef.current;
            if (cameras.length === 0) return;
            startPageStreams(cameras);
        }, 500); // 슬라이더 드래그 연속 이벤트 흡수
        return () => { if (playbackDebounceRef.current) clearTimeout(playbackDebounceRef.current); };
    }, [playbackTime, startPageStreams]);

    // 초기화 + unmount cleanup
    useEffect(() => {
        isMountedRef.current = true;
        (async () => {
            const [camResult, groupResult]: any[] = await Promise.all([
                getFetch("/api/cctv/cameras"),
                getFetch("/api/cctv/groups")
            ]);
            if (!isMountedRef.current) return;
            if (camResult?.data) {
                setCameraData(camResult.data);
                const firstBranch = Object.keys(camResult.data.branches)[0];
                if (firstBranch) setSelectedBranch(firstBranch);
            }
            if (groupResult?.data) setGroups(groupResult.data);
            setLoading(false);
        })();
        return () => {
            isMountedRef.current = false;
            currentBatchIdRef.current++; // in-flight startStream 전부 무효화 → self-stop
            stopAllStreams();
        };
    }, [stopAllStreams]);

    // 페이지/탭/모드 전환 시 확대 해제 (스트림은 startPageStreams가 정리)
    useEffect(() => {
        setExpandedCamId(null);
    }, [currentPage, selectedNvr, selectedGroupId, isLive]);

    // 지점 변경 시 첫 번째 NVR 자동 선택
    useEffect(() => {
        if (!cameraData || !selectedBranch) return;
        const firstNvr = Object.entries(cameraData.nvrs)
            .find(([, nvr]) => nvr.branch === selectedBranch);
        if (firstNvr) {
            setSelectedNvr(firstNvr[0]);
            setCurrentPage(1);
        }
    }, [selectedBranch, cameraData]);

    // perPage 변동 시 현재 페이지가 totalPages 초과하면 자동 리셋
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(1);
    }, [totalPages, currentPage]);

    // 녹화 시간 핸들러 — 날짜만 변경 (시/분/초 유지)
    const handleDateChange = (newDate: Date | null) => {
        if (!newDate) return;
        const next = new Date(playbackTime);
        next.setFullYear(newDate.getFullYear());
        next.setMonth(newDate.getMonth());
        next.setDate(newDate.getDate());
        setPlaybackTime(next);
    };

    // HH:MM 직접 입력 — 유효한 형식일 때만 반영
    const handleTimeInputChange = (timeStr: string) => {
        const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return;
        const h = parseInt(match[1]);
        const m = parseInt(match[2]);
        if (h < 0 || h > 23 || m < 0 || m > 59) return;
        const next = new Date(playbackTime);
        next.setHours(h);
        next.setMinutes(m);
        next.setSeconds(0);
        setPlaybackTime(next);
    };

    // 슬라이더 (0~1439분)
    const handleSliderChange = (_: Event, value: number | number[]) => {
        const minutes = Array.isArray(value) ? value[0] : value;
        const next = new Date(playbackTime);
        next.setHours(Math.floor(minutes / 60));
        next.setMinutes(minutes % 60);
        next.setSeconds(0);
        setPlaybackTime(next);
    };

    // 점프 버튼 공통
    const jumpMinutes = (delta: number) => {
        setPlaybackTime(t => new Date(t.getTime() + delta * 60 * 1000));
    };

    // 크게 보기: 라이브 모드에서만 HD 스트림으로 교체, 녹화 모드는 그대로
    const handleExpand = async (cam: Camera) => {
        setExpandedCamId(cam.id);
        if (!isLiveRef.current) return;

        const oldStreamName = camStreamRef.current.get(cam.id);
        if (oldStreamName) {
            activeStreamsRef.current.delete(oldStreamName);
            camStreamRef.current.delete(cam.id);
            postFetch("/api/cctv/stream/stop", { streamName: oldStreamName })
                .catch(e => console.error(`[cctv stop fail] ${oldStreamName}`, e));
        }

        try {
            const result: any = await postFetch("/api/cctv/live/start", { camId: cam.id, hd: true });
            if (result?.streamName) {
                if (!isMountedRef.current) {
                    postFetch("/api/cctv/stream/stop", { streamName: result.streamName })
                        .catch(e => console.error(`[cctv stop fail] ${result.streamName}`, e));
                    return;
                }
                activeStreamsRef.current.add(result.streamName);
                camStreamRef.current.set(cam.id, result.streamName);
                setEmbedUrls(prev => ({ ...prev, [cam.id]: result.embedUrl }));
            }
        } catch (e) {
            console.log(e);
        }
    };

    // 작게 보기: HD 스트림 stop → 서브스트림으로 복구
    const handleCollapse = async (camId: string) => {
        setExpandedCamId(null);
        if (!isLiveRef.current) return;

        const hdStreamName = camStreamRef.current.get(camId);
        if (hdStreamName) {
            activeStreamsRef.current.delete(hdStreamName);
            camStreamRef.current.delete(camId);
            postFetch("/api/cctv/stream/stop", { streamName: hdStreamName })
                .catch(e => console.error(`[cctv stop fail] ${hdStreamName}`, e));
        }

        const cam = pageCamerasRef.current.find(c => c.id === camId);
        if (!cam) return;

        try {
            const result: any = await postFetch("/api/cctv/live/start", { camId, hd: false });
            if (result?.streamName) {
                if (!isMountedRef.current) {
                    postFetch("/api/cctv/stream/stop", { streamName: result.streamName })
                        .catch(e => console.error(`[cctv stop fail] ${result.streamName}`, e));
                    return;
                }
                activeStreamsRef.current.add(result.streamName);
                camStreamRef.current.set(camId, result.streamName);
                setEmbedUrls(prev => ({ ...prev, [camId]: result.embedUrl }));
            }
        } catch (e) {
            console.log(e);
        }
    };

    // 그룹 체크박스 핸들러
    const handleGroupCheckboxChange = async (group: CctvGroup, checked: boolean) => {
        const newIds = checked
            ? [...group.camera_ids, groupSettingsCamera!.id]
            : group.camera_ids.filter(id => id !== groupSettingsCamera!.id);
        const result: any = await postFetch("/api/cctv/groups/edit", { id: group.id, cameraIds: newIds });
        if (result?.message === "success") {
            setGroups(prev => prev.map(g =>
                g.id === group.id ? { ...g, camera_ids: newIds } : g
            ));
        }
    };

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) return;
        const result: any = await postFetch("/api/cctv/groups", {
            name: newGroupName.trim(),
            cameraIds: []
        });
        if (result?.message === "success") {
            setGroups(prev => [...prev, result.data]);
            setNewGroupName("");
            setShowNewGroupModal(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 2, overflow: "auto" }}>
                <Backdrop open={loading} sx={{ zIndex: 9999, color: "#fff" }}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                {/* 1행: 지점 탭 */}
                <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    {cameraData && Object.entries(cameraData.branches).map(([key, branch]) => (
                        <Button
                            key={key}
                            size="small"
                            variant={selectedGroupId === null && selectedBranch === key ? "contained" : "outlined"}
                            onClick={() => { setSelectedGroupId(null); setSelectedBranch(key); }}
                        >
                            {branch.label}
                        </Button>
                    ))}
                </Box>

                {/* 2행: 그룹 탭 */}
                <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                    {groups.map(g => (
                        <Button
                            key={g.id}
                            size="small"
                            variant={selectedGroupId === g.id ? "contained" : "outlined"}
                            color="secondary"
                            onClick={() => { setSelectedGroupId(g.id); setCurrentPage(1); }}
                        >
                            {g.name}
                        </Button>
                    ))}
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => setShowNewGroupModal(true)}
                    >
                        ＋ 새 그룹
                    </Button>
                </Box>

                {/* 툴바 */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                    {selectedGroupId === null && cameraData && (
                        <>
                            <Typography variant="body2" fontWeight="bold">
                                {cameraData.branches[selectedBranch]?.label}
                            </Typography>
                            <FormControl size="small">
                                <Select
                                    value={selectedNvr}
                                    onChange={e => { setSelectedNvr(e.target.value); setCurrentPage(1); }}
                                >
                                    {Object.entries(cameraData.nvrs)
                                        .filter(([, nvr]) => nvr.branch === selectedBranch)
                                        .map(([key, nvr]) => (
                                            <MenuItem key={key} value={key}>{nvr.label}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                            <Typography variant="body2" color="text.secondary">
                                {currentCameras.length}개
                            </Typography>
                        </>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <ToggleButtonGroup
                        value={isLive ? "live" : "rec"}
                        exclusive
                        size="small"
                        onChange={(_, v) => {
                            if (v) {
                                setIsLive(v === "live");
                                setCurrentPage(1); // perPage 변동 시 1페이지로 리셋
                            }
                        }}
                    >
                        <ToggleButton value="live">실시간</ToggleButton>
                        <ToggleButton value="rec">녹화</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* 녹화 시간 컨트롤 */}
                {!isLive && (
                    <Box sx={{ width: '100%', mb: 2 }}>

                        {/* 상단 행: 날짜 선택 + 현재 시각 배지 + HH:MM 직접 입력 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                            <DatePicker
                                value={playbackTime}
                                onChange={handleDateChange}
                                inputFormat="yyyy-MM-dd"
                                renderInput={(params) => (
                                    <TextField {...params} size="small" sx={{ width: 160 }} />
                                )}
                            />
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center',
                                px: 1.5, py: 0.75,
                                bgcolor: 'primary.main', color: 'white',
                                borderRadius: 1, fontSize: 18, fontWeight: 600,
                                fontVariantNumeric: 'tabular-nums',
                                letterSpacing: 2,
                            }}>
                                {String(playbackTime.getHours()).padStart(2, '0')}:{String(playbackTime.getMinutes()).padStart(2, '0')}
                            </Box>
                            <TextField
                                size="small"
                                placeholder="HH:MM"
                                sx={{ width: 90 }}
                                onChange={e => handleTimeInputChange(e.target.value)}
                            />
                        </Box>

                        {/* 시간 슬라이더 */}
                        <Box sx={{ px: 1, mb: 1 }}>
                            <Slider
                                value={playbackTime.getHours() * 60 + playbackTime.getMinutes()}
                                min={0}
                                max={1439}
                                step={1}
                                marks={[
                                    { value: 0,    label: '00' },
                                    { value: 360,  label: '06' },
                                    { value: 720,  label: '12' },
                                    { value: 1080, label: '18' },
                                    { value: 1439, label: '24' },
                                ]}
                                onChange={handleSliderChange}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) =>
                                    `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`
                                }
                                size="small"
                            />
                        </Box>

                        {/* 점프 버튼 */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <ButtonGroup size="small" variant="outlined">
                                <Button onClick={() => jumpMinutes(-60)}>◀◀ 1시간</Button>
                                <Button onClick={() => jumpMinutes(-5)}>◀ 5분</Button>
                                <Button onClick={() => jumpMinutes(-1)}>◀ 1분</Button>
                            </ButtonGroup>
                            <ButtonGroup size="small" variant="outlined">
                                <Button onClick={() => jumpMinutes(1)}>1분 ▶</Button>
                                <Button onClick={() => jumpMinutes(5)}>5분 ▶</Button>
                                <Button onClick={() => jumpMinutes(60)}>1시간 ▶▶</Button>
                            </ButtonGroup>
                        </Box>

                    </Box>
                )}

                {/* 영상 그리드 */}
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${cols}, 1fr)`,
                    gridAutoFlow: "dense",
                    gap: 1,
                }}>
                    {pageCameras.map(cam => {
                        const isExpanded = expandedCamId === cam.id;
                        return (
                        <Box
                            key={cam.id}
                            onDoubleClick={() => isExpanded ? handleCollapse(cam.id) : handleExpand(cam)}
                            sx={{
                                position: "relative",
                                background: "#111",
                                borderRadius: 1,
                                overflow: "hidden",
                                cursor: "pointer",
                                "&:hover .cam-menu-btn": { opacity: 1 },
                                ...(isExpanded ? {
                                    gridColumn: "span 3",
                                    gridRow: "span 3",
                                } : {}),
                            }}
                        >
                            {embedUrls[cam.id]
                                ? (
                                    <Box sx={{ position: "relative", lineHeight: 0 }}>
                                        <iframe
                                            src={embedUrls[cam.id]}
                                            style={{
                                                width: "100%",
                                                aspectRatio: "16/9",
                                                border: "none",
                                                background: "#000",
                                                display: "block",
                                            }}
                                            allow="autoplay"
                                        />
                                        {/* iframe은 이벤트 버블링이 막히므로 더블클릭용 투명 오버레이 */}
                                        <Box
                                            onDoubleClick={() => isExpanded ? handleCollapse(cam.id) : handleExpand(cam)}
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                zIndex: 1,
                                            }}
                                        />
                                    </Box>
                                )
                                : (
                                    <Box sx={{
                                        aspectRatio: "16/9",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}>
                                        <CircularProgress size={24} sx={{ color: "#555" }} />
                                    </Box>
                                )
                            }
                            <IconButton
                                className="cam-menu-btn"
                                sx={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                    opacity: 0,
                                    transition: "opacity .2s",
                                    background: "rgba(0,0,0,.6)",
                                    color: "white",
                                    p: "3px",
                                    zIndex: 2,
                                    "&:hover": { opacity: 1, background: "rgba(0,0,0,.8)" },
                                }}
                                onClick={e => setCellMenuAnchor({ el: e.currentTarget, cam })}
                            >
                                ⋮
                            </IconButton>
                            <Box sx={{
                                position: "absolute",
                                bottom: 4,
                                left: 6,
                                color: "#ddd",
                                fontSize: 11,
                                textShadow: "0 1px 3px rgba(0,0,0,.9)",
                                pointerEvents: "none",
                            }}>
                                {cam.label}
                            </Box>
                            {selectedGroupId !== null && cameraData?.branches[cam.branch] && (
                                <Box sx={{
                                    position: "absolute",
                                    bottom: 4,
                                    right: 6,
                                    color: "#fff",
                                    fontSize: 16,
                                    fontWeight: 500,
                                    textShadow: "0 1px 4px rgba(0,0,0,1)",
                                    pointerEvents: "none",
                                }}>
                                    {cameraData.branches[cam.branch].label}
                                </Box>
                            )}
                        </Box>
                        );
                    })}
                </Box>

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mt: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_, p) => setCurrentPage(p)}
                            size="small"
                        />
                        <Typography variant="body2" color="text.secondary">
                            {currentCameras.length}개 중 {(currentPage - 1) * perPage + 1}~
                            {Math.min(currentPage * perPage, currentCameras.length)}
                            &nbsp;({currentPage}/{totalPages})
                        </Typography>
                    </Box>
                )}

                {/* 셀 컨텍스트 메뉴 */}
                <Menu
                    anchorEl={cellMenuAnchor?.el}
                    open={!!cellMenuAnchor}
                    onClose={() => setCellMenuAnchor(null)}
                >
                    {expandedCamId === cellMenuAnchor?.cam.id
                        ? (
                            <MenuItem onClick={() => { handleCollapse(cellMenuAnchor!.cam.id); setCellMenuAnchor(null); }}>
                                작게 보기
                            </MenuItem>
                        )
                        : (
                            <MenuItem onClick={() => { handleExpand(cellMenuAnchor!.cam); setCellMenuAnchor(null); }}>
                                크게 보기
                            </MenuItem>
                        )
                    }
                    <MenuItem onClick={() => {
                        setGroupSettingsCamera(cellMenuAnchor!.cam);
                        setCellMenuAnchor(null);
                    }}>
                        그룹 설정...
                    </MenuItem>
                    <MenuItem disabled>스냅샷 (TODO)</MenuItem>
                </Menu>

                {/* 그룹 설정 모달 */}
                <Dialog
                    open={!!groupSettingsCamera}
                    onClose={() => setGroupSettingsCamera(null)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>{groupSettingsCamera?.label} — 그룹 설정</DialogTitle>
                    <DialogContent>
                        {groups.length === 0
                            ? <Typography color="text.secondary" sx={{ pt: 1 }}>그룹이 없습니다.</Typography>
                            : groups.map(g => (
                                <FormControlLabel
                                    key={g.id}
                                    sx={{ display: "flex" }}
                                    control={
                                        <Checkbox
                                            checked={g.camera_ids.includes(groupSettingsCamera?.id || "")}
                                            onChange={e => handleGroupCheckboxChange(g, e.target.checked)}
                                        />
                                    }
                                    label={g.name}
                                />
                            ))
                        }
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setGroupSettingsCamera(null)}>닫기</Button>
                    </DialogActions>
                </Dialog>

                {/* 새 그룹 모달 */}
                <Dialog
                    open={showNewGroupModal}
                    onClose={() => { setShowNewGroupModal(false); setNewGroupName(""); }}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>새 그룹 만들기</DialogTitle>
                    <DialogContent>
                        <TextField
                            value={newGroupName}
                            onChange={e => setNewGroupName(e.target.value)}
                            label="그룹 이름"
                            fullWidth
                            autoFocus
                            sx={{ mt: 1 }}
                            onKeyDown={e => e.key === "Enter" && handleCreateGroup()}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => { setShowNewGroupModal(false); setNewGroupName(""); }}>취소</Button>
                        <Button
                            onClick={handleCreateGroup}
                            variant="contained"
                            disabled={!newGroupName.trim()}
                        >
                            만들기
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </LocalizationProvider>
    );
};

export default CctvMain;
